import { useCallback, useEffect, useMemo, useRef, useState } from "react";
const isMobileDevice = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { WHATSAPP_GROUP_LINK } from "@/lib/links";
import { cacheDataEqual, preloadImage, readPublicCache, writePublicCache } from "@/lib/publicCache";

const fallbackSlides = [
  {
    id: "fallback-secrets",
    title: "Secrets",
    description: "Uma experiência exclusiva, elegante e privada para viver noites memoráveis.",
    button_text: "Explorar Eventos",
    button_link: "#eventos",
    image_url: null,
    mobile_image_url: null,
    active: true,
    order: 0,
  },
  {
    id: "fallback-aniversario",
    title: "Faça seu aniversário no Secrets",
    description: "Uma experiência exclusiva, sofisticada e inesquecível para sua comemoração.",
    button_text: "Fazer Aniversário",
    button_link: "https://wa.me/554199875680?text=Oii%2C%20gostaria%20de%20fazer%20meu%20aniversario%20no%20Secrets.",
    image_url: null,
    mobile_image_url: null,
    active: true,
    order: 1,
  },
  {
    id: "fallback-casamento",
    title: "Celebre momentos únicos",
    description: "Eventos privados e experiências premium para casamentos especiais.",
    button_text: "Agendar Evento",
    button_link: "https://wa.me/554199875680?text=Oii%2C%20gostaria%20de%20agendar%20um%20evento%20no%20Secrets.",
    image_url: null,
    mobile_image_url: null,
    active: true,
    order: 2,
  },
  {
    id: "fallback-solteiro",
    title: "A despedida perfeita",
    description: "Noites exclusivas, privadas e memoráveis.",
    button_text: "Colocar Nome na Lista",
    button_link: "https://wa.me/554199875680?text=Oii%2C%20gostaria%20de%20colocar%20meu%20nome%20na%20lista%20do%20Secrets.",
    image_url: null,
    mobile_image_url: null,
    active: true,
    order: 3,
  },
];

const SLIDE_INTERVAL = 6500;
const MANUAL_SLIDE_INTERVAL = SLIDE_INTERVAL * 2;
const SLIDES_CACHE_KEY = "sc_hero_slides";
const SLIDES_CACHE_TTL = 10 * 60 * 1000;

const getCachedSlides = () => {
  const cached = readPublicCache(SLIDES_CACHE_KEY, { allowStale: true });
  return Array.isArray(cached) && cached.length ? cached : null;
};

export const HeroCarousel = () => {
  const [fetchedSlides, setFetchedSlides] = useState(() => getCachedSlides() || fallbackSlides);
  const [featuredEvent, setFeaturedEvent] = useState(null);
  const [current, setCurrent] = useState(0);
  const [manualDelay, setManualDelay] = useState(false);
  const [pointerStart, setPointerStart] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [slideDirection, setSlideDirection] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const sectionRef = useRef(null);
  const rafParallaxRef = useRef(null);

  const slides = useMemo(() => {
    if (featuredEvent?.enabled && featuredEvent?.show_as_slide) {
      const featuredSlide = {
        id: "featured-event",
        title: featuredEvent.title || "Evento Especial",
        description: featuredEvent.description || "",
        button_text: featuredEvent.button_text || "Saiba Mais",
        button_link: featuredEvent.button_link || "#eventos",
        image_url: featuredEvent.image_url || null,
        image_data: featuredEvent.image_data || null,
        mobile_image_url: featuredEvent.mobile_image_url || null,
        mobile_image_data: featuredEvent.mobile_image_data || null,
      };
      return [featuredSlide, ...fetchedSlides];
    }
    return fetchedSlides;
  }, [fetchedSlides, featuredEvent]);

  useEffect(() => {
    if (isMobileDevice) return; // parallax disabled on mobile — avoid continuous rAF + transform on mobile GPU
    const section = sectionRef.current;
    if (!section) return;
    const onScroll = () => {
      if (rafParallaxRef.current) return;
      rafParallaxRef.current = requestAnimationFrame(() => {
        const py = Math.min(window.scrollY * 0.3, 100);
        section.style.setProperty("--hero-py", `${py}px`);
        rafParallaxRef.current = null;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafParallaxRef.current) cancelAnimationFrame(rafParallaxRef.current);
    };
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    const nextIndex = (current + 1) % slides.length;
    const nextSlide = slides[nextIndex];
    if (!nextSlide) return;
    [nextSlide.image_url, nextSlide.mobile_image_url].forEach((src) => {
      preloadImage(src);
    });
  }, [current, slides]);

  useEffect(() => {
    let mounted = true;

    const fetchSlides = async () => {
      try {
        const res = await api.get("/hero-slides");
        if (mounted && res.data?.length) {
          writePublicCache(SLIDES_CACHE_KEY, res.data, SLIDES_CACHE_TTL);
          setFetchedSlides((prev) => {
            return cacheDataEqual(prev, res.data) ? prev : res.data;
          });
        }
      } catch (_) {}
    };

    const scheduleIdle = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 700));
    const cancelIdle = window.cancelIdleCallback || window.clearTimeout;
    const idleId = scheduleIdle(fetchSlides);
    return () => {
      mounted = false;
      cancelIdle(idleId);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    api.get("/featured-event").then((res) => {
      if (mounted && res.data?.enabled) setFeaturedEvent(res.data);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const timer = window.setTimeout(() => {
      setCurrent((index) => (index + 1) % slides.length);
      setManualDelay(false);
    }, manualDelay ? MANUAL_SLIDE_INTERVAL : SLIDE_INTERVAL);
    return () => window.clearTimeout(timer);
  }, [current, manualDelay, slides.length]);

  const slide = slides[current] || fallbackSlides[0];
  const imageSrc = slide.image_data || slide.image_url || null;
  const mobileImageSrc = slide.mobile_image_data || slide.mobile_image_url || imageSrc;

  const normalizedTitle = useMemo(() => {
    if (slide?.title?.toLowerCase() === "secrets") {
      return "brand-logo";
    }
    return slide?.title;
  }, [slide?.title]);

  const goToSlide = useCallback((nextIndex) => {
    if (!slides.length) return;
    setSlideDirection(nextIndex > current ? 1 : -1);
    setCurrent((nextIndex + slides.length) % slides.length);
    setManualDelay(true);
    setDragOffset(0);
  }, [current, slides.length]);

  const handleCta = () => {
    const link = slide?.button_link || "#eventos";
    if (link.startsWith("#")) {
      const el = document.querySelector(link);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      return;
    }
    if (link.startsWith("/")) {
      window.location.href = link;
      return;
    }
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const clearSelection = () => {
    try { window.getSelection?.().removeAllRanges(); } catch (_) {}
  };

  const isInteractiveTarget = (target) => {
    return Boolean(target?.closest?.("button, a, input, textarea, select, [role='button']"));
  };

  const handlePointerDown = (event) => {
    if (event.pointerType !== "mouse" || event.button !== 0) return;
    if (isInteractiveTarget(event.target)) return;
    setPointerStart(event.clientX);
    setIsDragging(true);
    setDragOffset(0);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    clearSelection();
  };

  const handlePointerMove = (event) => {
    if (pointerStart === null) return;
    if (event.pointerType !== "mouse") return;
    const offset = event.clientX - pointerStart;
    setDragOffset(Math.max(-180, Math.min(180, offset)));
    if (Math.abs(offset) > 6) {
      event.preventDefault();
      clearSelection();
    }
  };

  const handlePointerEnd = (event) => {
    if (pointerStart === null) return;
    if (event.pointerType !== "mouse") return;
    const offset = event.clientX - pointerStart;
    if (offset < -120) goToSlide(current + 1);
    else if (offset > 120) goToSlide(current - 1);
    else setDragOffset(0);
    setPointerStart(null);
    setIsDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    clearSelection();
  };

  const handleTouchEnd = (event) => {
    if (!touchStart) return;
    const touch = event.changedTouches?.[0];
    if (!touch) return;

    const offsetX = touch.clientX - touchStart.x;
    const offsetY = touch.clientY - touchStart.y;
    const isHorizontalSwipe = Math.abs(offsetX) > 48 && Math.abs(offsetX) > Math.abs(offsetY) * 1.25;

    if (isHorizontalSwipe) {
      goToSlide(offsetX < 0 ? current + 1 : current - 1);
    }
    setTouchStart(null);
  };

  return (
    <section
      ref={sectionRef}
      data-testid="hero-carousel"
      className={`relative flex min-h-[92vh] select-none items-center justify-center overflow-hidden bg-[#050505] md:min-h-screen ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
      onDragStart={(event) => event.preventDefault()}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerCancel={() => { setPointerStart(null); setDragOffset(0); setIsDragging(false); }}
      onPointerUp={handlePointerEnd}
      onTouchStart={(event) => {
        const touch = event.touches?.[0];
        if (touch) setTouchStart({ x: touch.clientX, y: touch.clientY });
      }}
      onTouchCancel={() => setTouchStart(null)}
      onTouchEnd={handleTouchEnd}
    >
      <AnimatePresence mode={isMobileDevice ? undefined : "wait"} custom={slideDirection}>
        <motion.div
          key={slide.id}
          custom={slideDirection}
          initial={isMobileDevice ? false : { opacity: 0, x: slideDirection * 90, scale: 1.02 }}
          animate={{ opacity: 1, x: dragOffset * 0.28, scale: 1 }}
          exit={isMobileDevice ? { opacity: 0 } : { opacity: 0, x: -slideDirection * 90, scale: 1.015 }}
          transition={{ duration: isDragging ? 0 : isMobileDevice ? 0.22 : 0.72, ease: [0.22, 0.68, 0, 1] }}
          className="absolute inset-0 pointer-events-none"
        >
          {/* Parallax wrapper — desktop only; mobile uses static positioning to avoid GPU pressure */}
          <div
            style={{
              position: "absolute",
              top: isMobileDevice ? 0 : "-100px",
              left: 0,
              right: 0,
              bottom: isMobileDevice ? 0 : "-100px",
              transform: isMobileDevice ? "none" : "translateY(var(--hero-py, 0px))",
              willChange: isMobileDevice ? "auto" : "transform",
            }}
          >
            {!imageSrc && !mobileImageSrc && (
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_18%,rgba(204,0,0,0.18),transparent_34%),radial-gradient(circle_at_82%_24%,rgba(201,164,92,0.10),transparent_24%),linear-gradient(180deg,#090909_0%,#050505_70%)]" />
            )}
            {imageSrc && (
              <motion.img
                src={imageSrc}
                alt={slide.title}
                className="hidden h-full w-full select-none object-cover object-center md:block"
                draggable={false}
                loading={current === 0 ? "eager" : "lazy"}
                fetchPriority={current === 0 ? "high" : "auto"}
                decoding="async"
                initial={{ scale: 1.04 }}
                animate={{ scale: 1.13 }}
                transition={{ duration: 8, ease: "easeOut" }}
              />
            )}
            {mobileImageSrc && (
              // No scale animation on mobile — eliminates continuous compositor animation on mobile GPU
              <img
                src={mobileImageSrc}
                alt={slide.title}
                className="block h-full w-full select-none object-cover object-center md:hidden"
                draggable={false}
                loading={current === 0 ? "eager" : "lazy"}
                fetchPriority={current === 0 ? "high" : "auto"}
                decoding="async"
              />
            )}
          </div>
          {/* backdrop-blur only on desktop — full-screen blur is very expensive on mobile GPU */}
          <div className="absolute inset-0 hidden backdrop-blur-[1px] md:block" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_18%,rgba(204,0,0,0.18),transparent_26%),radial-gradient(circle_at_76%_16%,rgba(201,164,92,0.08),transparent_18%),linear-gradient(180deg,rgba(0,0,0,0.76),rgba(0,0,0,0.42)_42%,#050505_100%)]" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#050505] to-transparent" />
        </motion.div>
      </AnimatePresence>

      <motion.div
        className="relative z-10 mx-auto max-w-6xl px-6 pt-20 text-center"
        animate={{ x: dragOffset * 0.08 }}
        transition={{ duration: isDragging ? 0 : 0.36, ease: "easeOut" }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={`${slide.id}-content`}
            initial={isMobileDevice ? false : { opacity: 0, y: 28, filter: "blur(8px)" }}
            animate={isMobileDevice ? { opacity: 1, y: 0 } : { opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={isMobileDevice ? { opacity: 0 } : { opacity: 0, y: -18, filter: "blur(8px)" }}
            transition={{ duration: isMobileDevice ? 0.25 : 0.8, ease: "easeOut" }}
          >
            <div className="mb-6 flex justify-center">
              <span className="luxury-pill">Exclusivo e Privado</span>
            </div>
            <h1
              className="flex justify-center font-['Cormorant_Garamond'] text-5xl font-normal tracking-tight text-white sm:text-6xl lg:text-[6.2rem]"
              data-testid="hero-title"
            >
              {normalizedTitle === "brand-logo" ? (
                <img
                  src="/logo%20(2).png"
                  alt="Secrets Club"
                  width="420"
                  height="167"
                  className="h-auto w-[min(82vw,520px)] object-contain drop-shadow-[0_18px_38px_rgba(0,0,0,0.55)]"
                  fetchPriority="high"
                  loading="eager"
                  decoding="sync"
                />
              ) : (
                <span className="flex flex-wrap justify-center">
                  {normalizedTitle.split(" ").map((word, i) => (
                    <motion.span
                      key={`${slide.id}-w${i}`}
                      initial={isMobileDevice ? false : { opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: isMobileDevice ? 0.2 : 0.6, delay: isMobileDevice ? 0 : 0.28 + i * 0.09, ease: "easeOut" }}
                      className="inline-block px-[0.1em]"
                    >
                      {word}
                    </motion.span>
                  ))}
                </span>
              )}
            </h1>
            <div className="mx-auto my-7 h-px w-24 bg-gradient-to-r from-transparent via-[#ff4545] via-[#CC0000] to-[#C9A45C]/45" />
            <p
              className="mx-auto mb-10 max-w-2xl font-['Manrope'] text-sm font-light leading-relaxed text-white/76 sm:text-lg"
              data-testid="hero-subtitle"
            >
              {slide.description}
            </p>
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <motion.button
                type="button"
                onClick={handleCta}
                whileHover={{ y: -2, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="hero-cta-premium cursor-pointer px-8 py-4 font-['Manrope'] text-xs uppercase tracking-[0.18em] sm:text-sm"
                data-testid="hero-cta-btn"
              >
                {slide.button_text}
              </motion.button>
              <motion.a
                href={WHATSAPP_GROUP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ y: -2, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex w-fit items-center justify-center gap-2 rounded-full border border-white/12 bg-white/8 px-6 py-3 font-['Manrope'] text-xs font-semibold uppercase tracking-[0.14em] text-white backdrop-blur transition-all duration-300 hover:border-[#C9A45C]/70 hover:bg-white/12"
                data-testid="hero-whatsapp-group-btn"
              >
                <MessageCircle size={15} />
                Entrar no grupo VIP
              </motion.a>
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {slides.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Slide anterior"
            onClick={() => goToSlide(current - 1)}
            className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/20 text-white/45 opacity-55 backdrop-blur transition-all duration-300 hover:border-[#CC0000]/60 hover:bg-[#CC0000] hover:text-white hover:opacity-100 md:left-8 md:h-11 md:w-11 md:text-white/75 md:opacity-100"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            aria-label="Próximo slide"
            onClick={() => goToSlide(current + 1)}
            className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/20 text-white/45 opacity-55 backdrop-blur transition-all duration-300 hover:border-[#CC0000]/60 hover:bg-[#CC0000] hover:text-white hover:opacity-100 md:right-8 md:h-11 md:w-11 md:text-white/75 md:opacity-100"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 items-center gap-3">
        {slides.map((item, index) => (
          <button
            key={item.id}
            type="button"
            aria-label={`Ir para slide ${index + 1}`}
            onClick={() => goToSlide(index)}
            className={`h-[3px] rounded-full transition-all duration-500 ${
              index === current ? "w-10 bg-[#CC0000]" : "w-5 bg-white/25"
            }`}
          />
        ))}
      </div>
    </section>
  );
};
